import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getSEO } from '../i18n/seo';
import { buildFaqSchema } from '../i18n/faq';

// Canonical production domain — override via REACT_APP_SITE_URL if deployed elsewhere.
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const HOTEL_NAME = 'Hotel La Rosa Negra';
const HOTEL_ADDRESS = 'Kepi i Gjeranave, Long Beach (Velika Plaža)';
const HOTEL_CITY = 'Ulcinj';
const HOTEL_POSTAL = '85360';

// hreflang alternates — must match /app/frontend/src/i18n/seo.js keys.
const HREFLANG_ALT = [
  { hreflang: 'en', code: 'en' },
  { hreflang: 'sr-ME', code: 'me' },
  { hreflang: 'sq', code: 'sq' },
  { hreflang: 'de', code: 'de' },
  { hreflang: 'ru', code: 'ru' },
  { hreflang: 'pl', code: 'pl' },
  { hreflang: 'hu', code: 'hu' },
];

// Build a per-locale URL.  Strips any existing language prefix from `path`,
// then prepends the requested locale path (or empty for `en` / x-default).
const stripLangPrefix = (path) => {
  const segs = (path || '/').split('/');
  if (segs.length > 1 && HREFLANG_ALT.some((a) => a.code === segs[1])) {
    segs.splice(1, 1);
  }
  const cleaned = segs.join('/') || '/';
  return cleaned === '' ? '/' : cleaned;
};

const buildAltUrl = (path, code) => {
  // One canonical form per page: no /index.html, no trailing slash (except the home page), no query string.
  const cleanPath = stripLangPrefix(path).split('?')[0].replace(/\/index\.html$/, '/').replace(/(.)\/+$/, '$1') || '/';
  const prefix = code === 'en' ? '' : `/${code}`;
  // Avoid double-slash when cleanPath is "/"
  const tail = cleanPath === '/' ? '/' : cleanPath;
  return `${SITE_URL}${prefix}${tail}`;
};

// Marker so we manage only the tags we own and don't disturb static ones we don't replace.
const MARK_ATTR = 'data-seohead';

const upsertMeta = (key, value) => {
  if (!value) return;
  const sep = key.indexOf(':');
  const attr = key.slice(0, sep); // 'name' or 'property'
  const name = key.slice(sep + 1); // e.g. 'og:title'
  const selector = `meta[${attr}="${name}"][${MARK_ATTR}]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    // Remove any pre-existing static meta with the same key so ours wins.
    document.head.querySelectorAll(`meta[${attr}="${name}"]`).forEach((m) => m.remove());
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute(MARK_ATTR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const upsertLink = (rel, attrs) => {
  // Find by rel only, then update its attributes (avoids creating duplicates).
  const selector = `link[rel="${rel}"][${MARK_ATTR}]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    // Drop the prerendered/static copy so the page never carries two canonicals.
    document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((l) => l.remove());
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MARK_ATTR, '1');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const replaceJsonLd = (key, schema) => {
  document.head.querySelectorAll(`script[${MARK_ATTR}="${key}"]`).forEach((s) => s.remove());
  if (!schema) return;
  const s = document.createElement('script');
  s.setAttribute('type', 'application/ld+json');
  s.setAttribute(MARK_ATTR, key);
  s.textContent = JSON.stringify(schema);
  document.head.appendChild(s);
};

const SEOHead = ({
  title,
  description,
  image,
  url, // path portion only, e.g. '/room/1'
  type = 'website',
  noindex = false,
  schema = null,
  keywords,
  faq = false, // hotel FAQPage graph only where the FAQ is actually on the page (home, /faq)
}) => {
  const { lang } = useLanguage();
  const localized = getSEO(lang);

  const seoTitle = title || localized.title;
  const seoDescription = description || localized.description;
  const seoImage = image ? (image.startsWith('/') ? `${SITE_URL}${image}` : image) : DEFAULT_OG_IMAGE;
  const path = url || '/';
  const canonical = buildAltUrl(path, lang);
  const seoKeywords = keywords || localized.keywords;

  useEffect(() => {
    // <html lang> + <title>
    document.documentElement.lang = localized.lang;
    document.title = seoTitle;

    // Core meta
    upsertMeta('name:description', seoDescription);
    upsertMeta('name:keywords', seoKeywords);
    upsertMeta('name:robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    // Canonical
    upsertLink('canonical', { href: canonical });

    // Open Graph (locale-varying)
    upsertMeta('property:og:type', type);
    upsertMeta('property:og:title', seoTitle);
    upsertMeta('property:og:description', seoDescription);
    upsertMeta('property:og:image', seoImage);
    upsertMeta('property:og:url', canonical);
    upsertMeta('property:og:site_name', HOTEL_NAME);
    upsertMeta('property:og:locale', localized.ogLocale);

    // Twitter (locale-varying)
    upsertMeta('name:twitter:card', 'summary_large_image');
    upsertMeta('name:twitter:title', seoTitle);
    upsertMeta('name:twitter:description', seoDescription);
    upsertMeta('name:twitter:image', seoImage);

    // og:locale:alternate — clear ALL (static + ours), then re-add the others
    document.head
      .querySelectorAll(`meta[property="og:locale:alternate"]`)
      .forEach((m) => m.remove());
    HREFLANG_ALT.filter((a) => a.code !== lang).forEach((a) => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:locale:alternate');
      m.setAttribute('content', getSEO(a.code).ogLocale);
      m.setAttribute(MARK_ATTR, '1');
      document.head.appendChild(m);
    });

    // hreflang per-page (use this page's path) — also clear any static ones we don't own
    document.head
      .querySelectorAll(`link[rel="alternate"][hreflang]`)
      .forEach((l) => l.remove());
    HREFLANG_ALT.forEach((alt) => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'alternate');
      l.setAttribute('hreflang', alt.hreflang);
      l.setAttribute('href', buildAltUrl(path, alt.code));
      l.setAttribute(MARK_ATTR, '1');
      document.head.appendChild(l);
    });
    const lDef = document.createElement('link');
    lDef.setAttribute('rel', 'alternate');
    lDef.setAttribute('hreflang', 'x-default');
    lDef.setAttribute('href', buildAltUrl(path, 'en'));
    lDef.setAttribute(MARK_ATTR, '1');
    document.head.appendChild(lDef);

    // Prerendered / static JSON-LD is for non-JS crawlers only — once React runs, the runtime owns every graph
    // (otherwise Google merges both copies of #hotel → "multiple aggregate ratings").
    document.head.querySelectorAll(`script[type="application/ld+json"]:not([${MARK_ATTR}]):not([id])`).forEach((s) => s.remove());

    // Page-specific schema (Hotel/Room/etc.)
    replaceJsonLd('per-page', schema);

    // Localised FAQPage — only on pages that render the FAQ (duplicate FAQ markup on every page is a quality signal against the site).
    replaceJsonLd('faq', faq ? buildFaqSchema(lang) : null);
  }, [
    seoTitle, seoDescription, seoImage, canonical, seoKeywords, type, noindex,
    localized.lang, localized.ogLocale, lang, path, schema, faq,
  ]);

  return null;
};

// Pre-built schema generators for different page types
export const generateRoomSchema = (room, lang = 'en') => ({
  "@context": "https://schema.org",
  "@type": "HotelRoom",
  "@id": `${SITE_URL}${lang === 'en' ? '' : `/${lang}`}/room/${room.id}`,
  "url": `${SITE_URL}${lang === 'en' ? '' : `/${lang}`}/room/${room.id}`,
  "inLanguage": lang === 'me' ? 'sr-ME' : lang,
  "dateModified": (room.updated_at || '').slice(0, 10) || undefined,
  "name": room.name,
  "description": room.description,
  "image": (room.images || []).slice(0, 6),
  "occupancy": {
    "@type": "QuantitativeValue",
    "maxValue": room.guests,
  },
  "floorSize": {
    "@type": "QuantitativeValue",
    "value": room.size,
    "unitCode": "MTK",
  },
  "amenityFeature": room.amenities?.map((amenity) => ({
    "@type": "LocationFeatureSpecification",
    "name": amenity,
    "value": true,
  })),
  "offers": {
    "@type": "Offer",
    "price": String(room.price),
    "priceCurrency": "EUR",
    "availability": "https://schema.org/InStock",
    "url": `${SITE_URL}${lang === 'en' ? '' : `/${lang}`}/booking?roomId=${room.id}`,
    "priceSpecification": {
      "@type": "PriceSpecification",
      "price": room.price,
      "priceCurrency": "EUR",
    },
  },
  "containedInPlace": {
    "@type": "Hotel",
    "@id": `${SITE_URL}/#hotel`,
    "name": HOTEL_NAME,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": HOTEL_ADDRESS,
      "addressLocality": HOTEL_CITY,
      "postalCode": HOTEL_POSTAL,
      "addressCountry": "ME",
    },
  },
});

export const generateBookingSchema = (checkIn, checkOut, roomName) => ({
  "@context": "https://schema.org",
  "@type": "LodgingReservation",
  "reservationStatus": "https://schema.org/ReservationPending",
  "reservationFor": {
    "@type": "LodgingBusiness",
    "name": HOTEL_NAME,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": HOTEL_ADDRESS,
      "addressLocality": HOTEL_CITY,
      "addressCountry": "ME",
    },
  },
  "checkinTime": checkIn,
  "checkoutTime": checkOut,
  "lodgingUnitDescription": roomName,
});

export default SEOHead;
